import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'contexts/AppContext';

/**
 * Custom hook for managing drag and drop functionality
 * Handles reordering of swimlanes and line items
 */
export const useDragAndDrop = ({
  swimlaneItems,
  setSwimLaneItems,
  refetchBudgetId,
  queryPayload,
}) => {
  const { useReorderSwimlane, useReorderLineItem } = useAppContext();
  const queryClient = useQueryClient();

  const setSwimlaneQueryCache = useCallback(
    nextItems => {
      if (!queryPayload?.id || !queryPayload?.type) return;

      queryClient.setQueryData(
        ['get-swimlanes-in-budget', { ...queryPayload }],
        previousData => {
          if (Array.isArray(previousData)) {
            return nextItems;
          }

          return {
            ...(previousData || {}),
            data: nextItems,
          };
        }
      );
    },
    [queryClient, queryPayload]
  );

  const handleDragEnd = useCallback(
    result => {
      if (!result.destination) return;

      const { source, destination } = result;
      const swimlane_item_Label = result?.draggableId
        ?.includes('swimlane')
        .toString();
      const line_item_Label = result?.draggableId
        ?.includes('line-item')
        .toString();

      if (source.index !== destination.index) {
        const reorderedItems = Array.from(swimlaneItems);
        const [removed] = reorderedItems.splice(source.index, 1);
        reorderedItems.splice(destination.index, 0, removed);

        const payload = reorderedItems.map((item, index) => ({
          id: item?.id,
          position: index,
        }));

        setSwimLaneItems(reorderedItems);
        setSwimlaneQueryCache(reorderedItems);

        if (swimlane_item_Label === 'true') {
          useReorderSwimlane.mutate(
            { swimlanes: payload },
            {
              onSuccess: () => {
                refetchBudgetId?.();
              },
            }
          );
        } else if (line_item_Label === 'true') {
          useReorderLineItem.mutate(
            { line_items: payload },
            {
              onSuccess: () => {
                refetchBudgetId?.();
              },
            }
          );
        }
      }
    },
    [
      swimlaneItems,
      setSwimLaneItems,
      setSwimlaneQueryCache,
      useReorderSwimlane,
      useReorderLineItem,
      refetchBudgetId,
    ]
  );

  return {
    handleDragEnd,
  };
};
